import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useEffectOnce } from 'react-use';

import { KnmListTableColumnsModal } from 'KnmListPackage/MainModule/components/KnmListTableColumnsModal';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { KnmListTable } from 'KnmListPackage/MainModule/components/KnmListTable';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import {
  erknmAnalyticsGetKnmList,
  erknmAnalyticsGetKnmListNextPage,
  erknmAnalyticsSetOrder,
} from 'AnalyticsPackage/ErknmAnalyticsModule/actions/erknmAnalyticsActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  analyticsGetData,
  analyticsKnmGetData,
  analyticsKnmGetDataNextPage,
  analyticsKnmSetFilter,
  analyticsKnmSetOrder,
  analyticsSetFilter,
} from 'AnalyticsPackage/MainModule/actions/analyticsActions';
import { IErknmAnalyticsFilter } from 'AnalyticsPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsFilter';
import { IAnalyticsFilter } from 'AnalyticsPackage/MainModule/interfaces/IAnalyticsFilter';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getUserKnoSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { AnalyticsPanel } from 'AnalyticsPackage/MainModule/components/AnalyticsPanel';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ROUTE_ANALYTICS_PROSECTOR_KNM } from 'CorePackage/MainModule/routes/routes';
import {
  erknmAnalyticsProsecutorOrganizationGetKnmList,
  erknmAnalyticsProsecutorOrganizationGetKnmListNextPage,
} from 'AnalyticsPackage/ErknmAnalyticsProsecutorOrganizationModule/actions/erknmAnalyticsProsecutorOrganizationActions';

import { initialAnalyticsKnmLIstTableColumns } from '../../../KnmListPackage/MainModule/constants/initialAnalyticsKnmLIstTableColumns';
import { getFilterFromParam } from '../common/getFilterFromParam';

const getErknmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const c = urlParams.get('c');
  const r = urlParams.get('r');
  const f = urlParams.get('f');
  const filterParams = getFilterFromParam<IErknmAnalyticsFilter>({});

  return { c, r, f, filterParams };
};

interface IAnalyticsKnmListProps {
  isErknm: boolean;
  knoOptions: IUserOrganisationSelectOption[];
  totalCount: number;
  data: IKnmRaw[];
  isOrganizationAnalytics: boolean;
}

const AnalyticsKnmListWithoutStore = ({
  isErknm,
  knoOptions,
  totalCount,
  data,
  isOrganizationAnalytics,
}: IAnalyticsKnmListProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isFilterReady, setIsFilterReady] = useState(false);
  const [isKnmListTableColumnsModalVisible, setIsKnmListTableColumnsModalVisible] = useState(false);
  const [tableColumns, setTableColumns] = useState<ITableColumn<IKnmRaw>[]>(initialAnalyticsKnmLIstTableColumns);

  const handleSettingsButtonClick = useCallback(() => setIsKnmListTableColumnsModalVisible(true), []);
  const closeKnmListTableColumnsModal = useCallback(() => setIsKnmListTableColumnsModalVisible(false), []);

  useEffectOnce(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.USER_KNOS));
    if (!isErknm) {
      const filterParams = getFilterFromParam<IAnalyticsFilter>({ includeChildProsec: true });

      dispatch(analyticsSetFilter(filterParams));
      dispatch(analyticsGetData());
    }
  });

  useEffect(() => {
    if (!isFilterReady) {
      if (isErknm) {
        setIsFilterReady(true);
      } else if (knoOptions.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const knoId = urlParams.get('knoId');
        const knmType = urlParams.get('knmType');
        const nameAdminLevel = urlParams.get('nameAdminLevel');
        const filterParams = getFilterFromParam<IAnalyticsFilter>({ includeChildProsec: true });
        const defaultKno = knoOptions.find(item => String(item.id) === knoId);
        const filter: IAnalyticsFilter = {
          ...filterParams,
          kno: defaultKno !== undefined ? [defaultKno] : filterParams.kno,
          knmType: knmType ?? undefined,
          nameAdminLevel: nameAdminLevel ?? undefined,
        };

        dispatch(analyticsKnmSetFilter(filter));
        setIsFilterReady(true);
      }
    }
  }, [knoOptions, isFilterReady, isErknm, dispatch]);

  useEffect(() => {
    if (isFilterReady) {
      if (isErknm) {
        dispatch(erknmAnalyticsSetOrder(getSortOrder(tableColumns)));
        const { c, r, f, filterParams } = getErknmParams();

        if (c && r && filterParams) {
          if (isOrganizationAnalytics) {
            dispatch(erknmAnalyticsProsecutorOrganizationGetKnmList(f, filterParams));
          } else {
            dispatch(erknmAnalyticsGetKnmList(r, c, f, filterParams));
          }
        }
      } else {
        dispatch(analyticsKnmSetOrder(getSortOrder(tableColumns)));
        dispatch(analyticsKnmGetData());
      }
    }
  }, [dispatch, isFilterReady, isErknm, tableColumns]);

  const getNextPage = useCallback(() => {
    if (isErknm) {
      const { c, r, f, filterParams } = getErknmParams();

      if (c && r && filterParams) {
        if (isOrganizationAnalytics) {
          dispatch(erknmAnalyticsProsecutorOrganizationGetKnmListNextPage(f, filterParams));
        } else {
          dispatch(erknmAnalyticsGetKnmListNextPage(r, c, f, filterParams));
        }
      }
    } else {
      dispatch(analyticsKnmGetDataNextPage());
    }
  }, [dispatch, isErknm]);

  return (
    <TablePage>
      <AnalyticsPanel isKnmList isOrganizationAnalytics={isOrganizationAnalytics} />
      <KnmListTable
        tableColumns={tableColumns}
        knms={data}
        totalCount={totalCount}
        isConfirmModalVisible={false}
        setTableColumns={setTableColumns}
        handleLoadClick={getNextPage}
        handleSettingsButtonClick={handleSettingsButtonClick}
      />
      <KnmListTableColumnsModal
        visible={isKnmListTableColumnsModalVisible}
        tableItems={tableColumns}
        onApply={setTableColumns}
        onClose={closeKnmListTableColumnsModal}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => {
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
  const isOrganizationAnalytics = window.location.pathname === ROUTE_ANALYTICS_PROSECTOR_KNM;

  if (isOrganizationAnalytics) {
    return {
      data: state.erknmAnalyticsProsecutorOrganization.knmList,
      totalCount: state.erknmAnalyticsProsecutorOrganization.knmTotalCount,
      knoOptions: getUserKnoSelectOptions(state),
      isErknm,
    };
  }

  return {
    data: isErknm ? state.erknmAnalytics.knmList : state.analytics.knmData,
    totalCount: isErknm ? state.erknmAnalytics.knmTotalCount : state.analytics.knmTotalCount,
    knoOptions: getUserKnoSelectOptions(state),
    isErknm,
  };
};

export const AnalyticsKnmList = connect(mapStateToProps)(AnalyticsKnmListWithoutStore);
